import React, { useState } from "react";

import InfoIcon from "@mui/icons-material/Info";
import { Box, Button, Grid, InputAdornment, Stack, TextField, useTheme } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { Job, TemplateSetAggregate } from "../../services/api";
import { UserRole } from "../../services/api/AuthUser";
import { getDuration, getLocalTime } from "../../services/utils/dateTime";
import { JobStatus as JobStatusEnum, parseJobStatus } from "../../services/utils/status";
import CopyToClipboardButton from "../shared/CopyToClipboardButton";
import ReadOnlyTextField, { readOnlyTextFieldStyles } from "../shared/ReadOnlyTextField";
import RoleBasedRender from "../shared/RoleBasedRender";
import { JobErrorAlert } from "./JobAlerts";
import JobStatus from "./JobStatus";

const JobDetails: React.FC<{
  job: Job;
  availableSets: TemplateSetAggregate[];
  onReTranscodeClick: () => void;
  onRegisterClick: () => Promise<void>;
  onRetryClick: () => Promise<void>;
}> = ({ job, availableSets, onReTranscodeClick, onRegisterClick, onRetryClick }) => {
  const theme = useTheme();
  const [templateSetsMap] = useState<Map<string, string>>(
    availableSets.reduce((map, item) => {
      map.set(item.id, item.disabled ? `${item.name} (deleted)` : item.name);
      return map;
    }, new Map<string, string>()),
  );
  const [actionButtonsDisabled, setActionButtonsDisabled] = useState(false);

  const handleRegisterClick = async () => {
    setActionButtonsDisabled(true);
    setTimeout(() => setActionButtonsDisabled(false), 5000);

    await onRegisterClick();
  };

  const handleRetryClick = async () => {
    setActionButtonsDisabled(true);
    setTimeout(() => setActionButtonsDisabled(false), 5000);

    await onRetryClick();
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" style={{ wordBreak: "break-all" }}>
          {`Job ${job._id}`}
        </Typography>
        <Box display="flex" alignItems="center">
          {parseJobStatus(job.status) === JobStatusEnum.ERROR && (
            <Tooltip title={job.error}>
              <InfoIcon sx={{ marginRight: "8px" }} />
            </Tooltip>
          )}
          <JobStatus status={parseJobStatus(job.status)} size="medium" variant="outlined" />
        </Box>
      </Stack>
      <Typography variant="subtitle1" color="gray">
        {getDuration(job.created_date, job.notification_date)}
      </Typography>

      {job.error && <JobErrorAlert error={job.error} />}

      <Grid container spacing={2} sx={{ paddingTop: "24px" }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Source"
            title={job.source}
            defaultValue={job.source}
            multiline
            maxRows={3}
            sx={readOnlyTextFieldStyles.root.clickable}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" onClick={(e) => e.stopPropagation()}>
                  <CopyToClipboardButton content={job.source} />
                </InputAdornment>
              ),
              readOnly: true,
              style: {
                cursor: "pointer",
                color: theme.palette.primary.main,
                textDecoration: "underline",
              },
            }}
            onClick={() => window.open(job.source, "_blank", "noopener,noreferrer")}
          ></TextField>
        </Grid>
        <Grid item xs={6}>
          <ReadOnlyTextField label="CreativeID" value={job.creative_id} fullWidth />
        </Grid>
        <Grid item xs={6}>
          <ReadOnlyTextField label="RenditionID" value={job.source_creative_rendition_id} fullWidth />
        </Grid>
        <Grid item xs={6}>
          <ReadOnlyTextField label="NetworkID" value={job.network_id} fullWidth />
        </Grid>
        <Grid item xs={6}>
          <ReadOnlyTextField label="Created Date" value={getLocalTime(job.created_date)} fullWidth />
        </Grid>
        {job.backfill_date && (
          <Grid item xs={6}>
            <ReadOnlyTextField label="Backfill Date" value={getLocalTime(job.backfill_date)} fullWidth />
          </Grid>
        )}
        {job.deactivation_date && (
          <Grid item xs={6}>
            <ReadOnlyTextField
              label="Deactivation Date"
              value={getLocalTime(job.deactivation_date)}
              fullWidth
            />
          </Grid>
        )}
        <Grid item xs={6}>
          <ReadOnlyTextField label="Dry Run" value={job.dry_run} fullWidth />
        </Grid>
        {job.set_id && (
          <Grid item xs={6}>
            <ReadOnlyTextField
              label="Set"
              value={job.set_id ? `${job.set_id} - ${templateSetsMap.get(job.set_id) || "invalid set"}` : " "}
              fullWidth
            />
          </Grid>
        )}
      </Grid>
      <Grid sx={{ paddingTop: "12px" }}>
        <Stack direction="row" alignItems="flex-end" justifyContent="flex-end" spacing={1}>
          <RoleBasedRender requiredRoles={[UserRole.ADMIN, UserRole.OPERATOR]}>
            {job.canReTranscodeJob() && (
              <Button variant="contained" onClick={onReTranscodeClick} sx={{ mr: "8px" }}>
                Re-Transcode
              </Button>
            )}
            {job.canRegisterAssets() && (
              <Button variant="contained" disabled={actionButtonsDisabled} onClick={handleRegisterClick}>
                Register
              </Button>
            )}
            {job.status === JobStatusEnum.ERROR && (
              <Button variant="contained" disabled={actionButtonsDisabled} onClick={handleRetryClick}>
                Retry
              </Button>
            )}
          </RoleBasedRender>
        </Stack>
      </Grid>
    </Box>
  );
};

export default JobDetails;
